<template>
    <Head title="My Work" />
    <BaseLayout gap="lg">
        <BasePageHeader
            title="My Work"
            subtitle="Each week, Nadia will provide personalized insights based on knowledge of you. These insights will improve as context about you grows." />

        <div class="flex flex-1 flex-col gap-6">
            <!-- Main layout -->
            <div class="grid gap-6 lg:grid-cols-5 lg:items-start">
                <div v-if="experiments.length" class="col-span-full order-2 lg:order-1">
                    <h2 class="title-body text-slate-900">Your Insights</h2>
                </div>

                <section class="flex flex-col gap-6 order-3 lg:order-2 min-w-0" :class="showSidebar ? 'lg:col-span-3' : 'lg:col-span-5'">
                    <!-- No-insights empty state -->
                    <template v-if="!experiments.length">
                        <div class="flex flex-col gap-4">
                            <!-- Tier 1: Calendar -->
                            <ContextActivationTier
                                :tier="1"
                                title="Calendar Intelligence"
                                subtitle="Connect your calendar to get insights on your key meetings and work rhythm"
                                :completed="tier1Complete"
                                :unlocked="true"
                                :preview-insights="tier1PreviewInsights"
                                @cta-click="handleTier1Cta" />

                            <!-- Tier 2: Perspective (only shown when FF is on) -->
                            <ContextActivationTier
                                v-if="perspectiveEnabled"
                                :tier="2"
                                title="Perspective On You"
                                subtitle="Complete Perspective to get insights on your working style and relationships"
                                :completed="tier2Complete"
                                :unlocked="tier1Complete"
                                :preview-insights="tier2PreviewInsights"
                                @cta-click="handleTier2Cta" />

                            <!-- Tier 3: Collaboration Profile -->
                            <ContextActivationTier
                                v-if="public_profile_ff_enabled"
                                :tier="3"
                                title="Collaboration Profile"
                                subtitle="Add Collaboration Profiles to enable insights on team dynamics and initiative patterns"
                                :completed="tier3Complete"
                                :unlocked="tier1Complete"
                                :preview-insights="tier3PreviewInsights"
                                @cta-click="handleTier3Cta">
                                <template #title-suffix>
                                    <CollaborationProfileTooltip />
                                </template>
                            </ContextActivationTier>

                            <!-- Tier 4: Work IQ Intelligence -->
                            <ContextActivationTier
                                v-if="workiq_ff_enabled"
                                :tier="4"
                                title="Work IQ Intelligence"
                                subtitle="Connect Work IQ to unlock insights from your Microsoft 365 signals — email, Teams, and calendar"
                                :completed="tier4Complete"
                                :unlocked="true"
                                :preview-insights="tier4PreviewInsights"
                                @cta-click="handleTier4Cta" />
                        </div>
                    </template>

                    <!-- Insights available -->
                    <template v-else>
                        <div class="flex flex-col gap-6">
                            <MyWorkInsightCard
                                v-for="{ experiment, insightWithFeedback } in orderedInsights"
                                :key="insightWithFeedback.insight.id"
                                :experiment-title="experiment.title"
                                :experiment-slug="experiment.slug"
                                :title="insightWithFeedback.insight.title"
                                :summary="insightWithFeedback.insight.summary"
                                :content="insightWithFeedback.insight.content"
                                :insight-id="insightWithFeedback.insight.id"
                                :unknowns="insightWithFeedback.insight.unknowns"
                                :created-at="insightWithFeedback.insight.created_at"
                                :perspective-enabled="perspectiveEnabled"
                                :collaboration-profile-enabled="public_profile_ff_enabled"
                                :work-iq-enabled="workiq_ff_enabled"
                                :calendar-enabled="calendarIntegrationEnabled"
                                :perspective-present="has_perspective"
                                :collaboration-profile-present="has_collaboration_profile"
                                :work-iq-present="has_workiq_connected"
                                :evidence-groups="insightWithFeedback.insight.evidence_groups"
                                :show-see-more="true" />
                        </div>
                    </template>
                </section>

                <!-- Right column: Perspective nudge + Sharing card stacked, below insights on mobile -->
                <section v-if="showSidebar" class="flex flex-col gap-6 lg:col-span-2 lg:self-start order-3 min-w-0">
                    <PerspectiveNudgeCard v-if="showPerspectiveNudge" @cta-click="handlePerspectiveNudgeCta" />

                    <!-- Smart profile-linking suggestions (ENG-1400) -->
                    <!-- Both flags off ⇒ no profile can ever resolve as a suggestion (see effective_access) -->
                    <ProfileLinkSuggestionsCard
                        v-if="showProfileLinkSuggestions"
                        ref="linkProfilesRef"
                        :initial-linked-count="pipeline_linked_profiles_count"
                        :own-profile-share="own_profile_share"
                        :org-has-ms-teams="org_has_ms_teams"
                        :profile-link-sharing-enabled="profile_link_sharing_ff_enabled"
                        :surface="profileLinkSuggestionsCardSurface"
                        @create-profile-shown="handleCreateProfilePromptShown"
                        @create-profile-click="handleCreateProfilePromptClicked" />
                </section>
            </div>
        </div>
    </BaseLayout>

    <CalendarIntegrationSettings v-model:open="showCalendarDialog" @close="showCalendarDialog = false" />
</template>

<script lang="ts">
import LeftSidebarLayout from "~vue/layouts/LeftSidebarLayout.vue";
export default { layout: LeftSidebarLayout };
</script>

<script setup lang="ts">
import { Head, router, usePage } from "@inertiajs/vue3";
import { useFetch } from "~vue/api";
import { dismissNotificationLocallyKey, type PerspectiveReport } from "~vue/appTypes";
import CollaborationProfileTooltip from "~vue/CollaborationProfile/CollaborationProfileTooltip.vue";
import BaseLayout from "~vue/components/BaseLayout.vue";
import BasePageHeader from "~vue/components/BasePageHeader.vue";
import CalendarIntegrationSettings from "~vue/components/CalendarIntegration/CalendarIntegrationSettings.vue";
import { MainMenuPane } from "~vue/components/navigation/menuPanes";
import { usePerspectiveReportImport } from "~vue/composables/usePerspectiveReportImport";
import ContextActivationTier from "~vue/Labs/ContextActivationTier.vue";
import { MYWORK_CTA_SOURCES, TIER_LABELS } from "~vue/Labs/insightCategory";
import { type MyWorkInsightRenderItem, orderMyWorkInsights } from "~vue/Labs/insightOrdering";
import MyWorkInsightCard from "~vue/Labs/MyWorkInsightCard.vue";
import PerspectiveNudgeCard from "~vue/Labs/PerspectiveNudgeCard.vue";
import ProfileLinkSuggestionsCard from "~vue/Labs/ProfileLinkSuggestionsCard.vue";
import type { OwnProfileShare } from "~vue/Labs/profileLinkTypes";
import type { LabExperiment } from "~vue/Labs/types";
import { useOpenPerspectiveSession } from "~vue/Perspective/composable/useOpenPerspectiveSession";
import { useCalendarStore } from "~vue/stores/calendarStore";
import type { CalendarIntegrationViewEvent } from "~vue/types/calendar_integration";
import type { ChatEntranceUseCaseData } from "~vue/types/chat";
import { openUrl } from "~vue/utils";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { computed, inject, nextTick, onMounted, provide, ref, useTemplateRef } from "vue";

const props = withDefaults(
    defineProps<{
        experiments?: LabExperiment[];
        calendar_integration?: { enabled?: boolean } | null;
        calendar_demo_data_enabled?: boolean;
        initial_calendar_events?: CalendarIntegrationViewEvent[] | null;
        calendar_coach_use_case?: ChatEntranceUseCaseData | null;
        has_integrated_calendar?: boolean;
        has_perspective?: boolean;
        perspective_report?: PerspectiveReport | null;
        perspective_assessment_enabled?: boolean;
        perspective_report_enabled?: boolean;
        public_profile_ff_enabled?: boolean;
        memory_connections_ff_enabled?: boolean;
        profile_link_sharing_ff_enabled?: boolean;
        org_profile_sharing_ff_enabled?: boolean;
        mywork_profile_link_suggestions_ff_enabled?: boolean;
        pipeline_linked_profiles_count?: number;
        own_profile_share?: OwnProfileShare | null;
        org_has_ms_teams?: boolean;
        has_collaboration_profile?: boolean;
        workiq_ff_enabled?: boolean;
        has_workiq_connected?: boolean;
        workiq_connect_url?: string;
    }>(),
    {
        experiments: () => [],
        calendar_integration: () => ({}),
        calendar_demo_data_enabled: false,
        initial_calendar_events: null,
        calendar_coach_use_case: null,
        has_integrated_calendar: false,
        has_perspective: false,
        perspective_report: null,
        perspective_assessment_enabled: false,
        perspective_report_enabled: false,
        public_profile_ff_enabled: false,
        memory_connections_ff_enabled: false,
        profile_link_sharing_ff_enabled: false,
        org_profile_sharing_ff_enabled: false,
        mywork_profile_link_suggestions_ff_enabled: false,
        pipeline_linked_profiles_count: 0,
        own_profile_share: null,
        org_has_ms_teams: false,
        has_collaboration_profile: false,
        workiq_ff_enabled: false,
        has_workiq_connected: false,
        workiq_connect_url: "/workiq/connect/",
    },
);

const page = usePage();

const profileLinkSuggestionsCardSurface = "my_work";

const experiments = ref<LabExperiment[]>(props.experiments ?? []);
const flattenedInsights = computed<MyWorkInsightRenderItem[]>(() =>
    experiments.value.flatMap((experiment) =>
        experiment.insights.map((insightWithFeedback) => ({
            experiment: {
                id: experiment.id,
                slug: experiment.slug,
                title: experiment.title,
            },
            insightWithFeedback,
        })),
    ),
);
const orderedInsights = computed(() => orderMyWorkInsights(flattenedInsights.value));
const calendarIntegrationEnabled = computed(() => Boolean(props.calendar_integration?.enabled));

// With no insights the main column shows the activation tiers instead, so a sidebar nudge to
// sharpen insights that don't exist yet has nothing to act on. Calendar is the first gate on
// insights, so profile-linking suggestions also wait for it (BRA-871). Both sharing flags off ⇒
// no profile can ever resolve as a suggestion (see effective_access).
const showProfileLinkSuggestions = computed(
    () =>
        orderedInsights.value.length > 0 &&
        props.has_integrated_calendar &&
        props.public_profile_ff_enabled &&
        props.memory_connections_ff_enabled &&
        props.mywork_profile_link_suggestions_ff_enabled &&
        (props.org_profile_sharing_ff_enabled || props.profile_link_sharing_ff_enabled),
);

const perspectiveEnabled = computed(() => props.perspective_assessment_enabled || props.perspective_report_enabled);
const showPerspectiveNudge = computed(() => orderedInsights.value.length > 0 && perspectiveEnabled.value && !props.has_perspective && props.has_integrated_calendar);
const showSidebar = computed(() => !props.calendar_demo_data_enabled && (showPerspectiveNudge.value || showProfileLinkSuggestions.value));

// Prefer the in-app assessment when its flag is on; otherwise fall back to the
// external Team Tools perspective questionnaire. Only referenced from the tier 2
// CTA, which is itself gated on perspectiveEnabled.
const perspectiveCtaUrl = computed(() => (props.perspective_assessment_enabled ? "/perspective/" : `${page.props.align_host}/perspective/questions`));

const { openSession: openPerspectiveSession } = useOpenPerspectiveSession();

// Poll for a Team Tools perspective report in the background. When one is found,
// reload has_perspective so the tier-2 activation card updates without a full refresh.
usePerspectiveReportImport({
    reportEnabled: () => props.perspective_report_enabled,
    assessmentEnabled: () => props.perspective_assessment_enabled,
    initialReport: props.perspective_report,
    onReportFound: () => router.reload({ only: ["has_perspective"] }),
});

provide("calendarIntegration", props.calendar_integration);

const showCalendarDialog = ref(false);
function handleTier1Cta() {
    logUserInteraction(AmplitudeEventNames.MYWORK_ACTIVATOR_CTA_CLICKED, { cta_label: TIER_LABELS[1], tier: 1, source: MYWORK_CTA_SOURCES.PREVIEW_CARD });
    useCalendarStore.setShowConnectCalendarDialog(true);
    showCalendarDialog.value = true;
}

function handleTier2Cta() {
    logUserInteraction(AmplitudeEventNames.MYWORK_ACTIVATOR_CTA_CLICKED, { cta_label: TIER_LABELS[2], tier: 2, source: MYWORK_CTA_SOURCES.PREVIEW_CARD });
    globalThis.open(perspectiveCtaUrl.value, "_blank");
}

// Opening a session first makes /perspective/ land on the questions rather than the intro.
// If that fails the tab still opens on the intro, whose own Start button surfaces the error.
async function handlePerspectiveNudgeCta() {
    logUserInteraction(AmplitudeEventNames.MYWORK_ACTIVATOR_CTA_CLICKED, {
        cta_label: TIER_LABELS[2],
        tier: 2,
        source: MYWORK_CTA_SOURCES.PERSPECTIVE_NUDGE_CARD,
    });
    if (props.perspective_assessment_enabled) {
        await openPerspectiveSession();
    }
    globalThis.open(perspectiveCtaUrl.value, "_blank");
}

function handleTier3Cta() {
    logUserInteraction(AmplitudeEventNames.MYWORK_ACTIVATOR_CTA_CLICKED, { cta_label: TIER_LABELS[3], tier: 3, source: MYWORK_CTA_SOURCES.PREVIEW_CARD });
    globalThis.open(tier3Cta.href, "_blank");
}

async function handleTier4Cta() {
    // Await the analytics POST before the same-tab OAuth redirect, or the document unload aborts
    // it and drops the click event. logUserInteraction never rejects.
    await logUserInteraction(AmplitudeEventNames.MYWORK_ACTIVATOR_CTA_CLICKED, { cta_label: TIER_LABELS[4], tier: 4, source: MYWORK_CTA_SOURCES.PREVIEW_CARD });
    globalThis.location.href = props.workiq_connect_url;
}

// ProfileLinkSuggestionsCard bubbles these past itself (it's reused across surfaces), so My Work
// logs its own contextual event name here rather than sharing one with other mount points.
function handleCreateProfilePromptShown() {
    logUserInteraction(AmplitudeEventNames.PROFILE_LINK_CREATE_PROFILE_PROMPT_SHOWN, { surface: profileLinkSuggestionsCardSurface });
}

async function handleCreateProfilePromptClicked() {
    // Await the analytics POST before navigating away, or the same-tab route change can abort it
    // and drop the click event. logUserInteraction never rejects.
    await logUserInteraction(AmplitudeEventNames.PROFILE_LINK_CREATE_PROFILE_PROMPT_CLICKED, { surface: profileLinkSuggestionsCardSurface });
    openUrl(props.has_perspective ? "/profile/create?auto_generate=true" : "/profile/create");
}
const dismissNotificationLocally = inject(dismissNotificationLocallyKey, () => {});

const { execute: recordVisit } = useFetch("/v1/navigation/notifications/dismiss", { immediate: false }).post({ pane: "my_work", track_all_visits: true }).json();

const linkProfilesRef = useTemplateRef("linkProfilesRef");
const LINK_PROFILES_HASH = "#link-profiles";

const scrollToLinkProfileCard = async () => {
    if (globalThis.location.hash === LINK_PROFILES_HASH) {
        await nextTick();
        linkProfilesRef.value?.$el?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
};

onMounted(async () => {
    dismissNotificationLocally(MainMenuPane.MY_WORK);
    recordVisit();
    logUserInteraction(AmplitudeEventNames.MYWORK_PAGE_SHOWN, {
        insight_count: experiments.value.flatMap((e) => e.insights).length,
        activations: [
            "calendar",
            ...(perspectiveEnabled.value ? ["perspective"] : []),
            ...(props.public_profile_ff_enabled ? ["collab_profile"] : []),
            ...(props.workiq_ff_enabled ? ["workiq"] : []),
        ],
        activation_states: {
            calendar: props.has_integrated_calendar,
            ...(perspectiveEnabled.value ? { perspective: props.has_perspective } : {}),
            ...(props.public_profile_ff_enabled ? { collab_profile: props.has_collaboration_profile } : {}),
            ...(props.workiq_ff_enabled ? { workiq: props.has_workiq_connected } : {}),
        },
    });
    scrollToLinkProfileCard();
});

// Activation completion state
const tier1Complete = computed(() => props.has_integrated_calendar);
const tier2Complete = computed(() => props.has_perspective);
const tier3Complete = computed(() => props.has_collaboration_profile);
const tier4Complete = computed(() => props.has_workiq_connected);

const tier3Cta = { label: "Complete Profile", href: "/profile/create" };

// Preview content uses real experiment categories from the insight pipeline
const tier1PreviewInsights = [
    {
        category: "High-Stakes Meeting Prep",
        title: "LT Check-In — your voice may get lost in a room of 9",
        summary: "Friday's leadership meeting has 9 people in 45 minutes. If there's something engineering needs heard, you may need to be more assertive than feels natural.",
    },
    {
        category: "Project Risk Radar",
        title: "Hiring velocity may be outpacing onboarding capacity",
        summary: "You've brought on 5 engineers this month while running 8 meetings a day. Nadia flags when scaling speed creates onboarding risk.",
    },
];

const tier2PreviewInsights = [
    {
        category: "Calendar Mismatch",
        title: "Tomorrow's 1:1 marathon — eight conversations, no buffer",
        summary:
            "8 back-to-back 1:1s with only a lunch break. Your Focus Time block is missing, and your profile suggests you need processing time between high-weight conversations.",
    },
    {
        category: "Manager Alert",
        title: "Marcus — workload & wellbeing",
        summary:
            "Marcus appears in 40+ meetings across 6 initiatives. His stress pattern is to become hypercritical. Tomorrow's 1:1 is titled 'Workload & Wellbeing' — Nadia surfaces the right questions to ask.",
    },
];

const tier3PreviewInsights = [
    {
        category: "Coaching Card — Team Tension",
        title: "David & Elena — delivery speed vs. architecture quality",
        summary: "David and Elena sit on opposite ends of a key dimension. Without you mediating, this could turn into passive friction. Their 1:1s are back-to-back tomorrow.",
    },
    {
        category: "High-Stakes Meeting Prep",
        title: "Room composition for Friday's LT — where your style is rare",
        summary: "With team profiles, Nadia maps the personality composition of each meeting and flags where your approach may clash or complement.",
    },
];

const tier4PreviewInsights = [
    {
        category: "Workload",
        title: "Heavy Week of Recurring Collaboration Meetings",
        summary:
            "Your calendar this week is dominated by recurring collaboration and coordination meetings, with little evidence of large focus-time blocks. This pattern could impact your ability to make headway on deep work or strategic tasks.",
    },
    {
        category: "Meeting Prep",
        title: "Group Meetings May Need Pre-Read",
        summary:
            "Several planning and technical review meetings on your calendar are likely to benefit from advance preparation, especially where decisions are expected. Checking for pre-reads or aligning with stakeholders beforehand could help you make the most of these sessions.",
    },
    {
        category: "Relationship",
        title: "Sustained Collaboration with Core Team Members",
        summary:
            "You continue to have frequent touchpoints with a few key collaborators. This could be a good week to check in on alignment or unblock any hidden issues within this core group.",
    },
];
</script>
